'use client';

import useResizeObserver from '@react-hook/resize-observer';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import AnimationVector from '@commons/assets/textures/topo-pattern.svg';
import { useResizeObserverSSROptions } from '@commons/hooks/useSize';

import styles from './index.module.scss';

// Type definition for the Texture component properties.

type Props = {
  animated?: boolean;
  className?: string;
};

/**
 * Texture component.
 *
 * Uses a ResizeObserver to obtain the parent element's size and adjust the texture size accordingly.
 *
 * @see https://github.com/jaredLunde/react-hook/tree/master/packages/resize-observer
 */

const Texture = ({ animated = false, className }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [parentElement, setParentElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      setParentElement(ref.current.parentElement);
    }
  }, []);

  useResizeObserver(
    parentElement,
    (entry) => {
      if (ref.current) {
        const { clientWidth, clientHeight } = entry.target;
        const size = Math.max(clientWidth, clientHeight);

        const wrapper = ref.current.querySelector('div');
        if (wrapper) {
          wrapper.style.width = `${size}px`;
          wrapper.style.height = `${size}px`;

          if (!wrapper.classList.contains(styles.loaded)) {
            wrapper.classList.add(styles.loaded);
          }
        }
      }
    },
    useResizeObserverSSROptions
  );

  const classes = clsx(styles.textureContainer, animated && styles.animated, className);

  return (
    <div ref={ref} className={classes}>
      <div className={styles.wrapper}>
        <AnimationVector />
      </div>
    </div>
  );
};

export default Texture;
